// ═══════════════════════════════════════════════════════════════
// TWO-FACTOR — a code from your phone, on top of the password
// ═══════════════════════════════════════════════════════════════
// MOS holds live bank feeds. Until now the only thing between a stolen
// password and every account Marcstone has was the password. This adds the
// second factor: a six-digit code from an authenticator app, which changes
// every thirty seconds and never travels over email or SMS.
//
// Supabase does NOT enforce this on its own. signInWithPassword succeeds and
// hands back a session at aal1 even when a verified factor exists — it is the
// application's job to notice that a step-up is owed and ask for it. That is
// what needsChallenge() is for.
//
// Worth being straight about the limit of a screen-level gate: an aal1 session
// still carries a real JWT, so anyone holding it could call the API directly.
// Closing that means requiring aal2 server-side on the tier-3 routes, which is
// a deliberate second step — flipping it before Drew is enrolled would lock
// the owner out of his own books.

import { supabase } from '../context/AuthContext';

export interface MfaFactor {
  id: string;
  friendlyName: string | null;
  createdAt: string;
}

export interface MfaEnrollment {
  factorId: string;
  /** An SVG data URI straight from Supabase — render it in an <img>, no
   *  QR library and no third-party request. */
  qrCode: string;
  /** The same secret in text, for typing in by hand when a camera is not
   *  an option (a desktop authenticator, or a phone that will not focus). */
  secret: string;
}

function fail(error: { message: string } | null, fallback: string): never | void {
  if (error) throw new Error(error.message || fallback);
}

/** Factors that are actually usable. Supabase's `totp` list is verified-only;
 *  an abandoned half-enrollment lives in `all` and must not count. */
export async function listVerifiedFactors(): Promise<MfaFactor[]> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  fail(error, 'Could not read two-factor status');
  return (data?.totp ?? []).map((f) => ({
    id: f.id,
    friendlyName: f.friendly_name ?? null,
    createdAt: f.created_at,
  }));
}

/** True when this session is owed a step-up: a verified factor exists but the
 *  session has not been raised to aal2 yet. */
export async function needsChallenge(): Promise<boolean> {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) return false; // fail open — see the header note
  return data.nextLevel === 'aal2' && data.currentLevel !== 'aal2';
}

/** The id to challenge against. Any verified factor will do; we keep one. */
export async function primaryFactorId(): Promise<string | null> {
  const factors = await listVerifiedFactors();
  return factors[0]?.id ?? null;
}

/**
 * Start enrolling. Half-finished attempts are swept first: Supabase rejects a
 * duplicate friendly name, so a user who closed the dialog mid-setup would
 * otherwise be unable to start again — which reads as "two-factor is broken".
 */
export async function beginEnrollment(friendlyName = 'Authenticator'): Promise<MfaEnrollment> {
  const { data: existing } = await supabase.auth.mfa.listFactors();
  const verified = new Set((existing?.totp ?? []).map((f) => f.id));
  for (const f of existing?.all ?? []) {
    if (!verified.has(f.id)) {
      await supabase.auth.mfa.unenroll({ factorId: f.id }).catch(() => undefined);
    }
  }

  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName,
  });
  fail(error, 'Could not start two-factor setup');
  if (!data?.totp) throw new Error('Supabase returned no enrollment code');
  return { factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret };
}

/** Prove the app is set up correctly. Only after this does the factor count. */
export async function confirmEnrollment(factorId: string, code: string): Promise<void> {
  const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
  fail(error, 'That code was not accepted');
}

/** Closing the dialog mid-setup leaves an unverified factor behind. Drop it so
 *  the next attempt starts clean rather than colliding with this one. */
export async function cancelEnrollment(factorId: string): Promise<void> {
  await supabase.auth.mfa.unenroll({ factorId }).catch(() => undefined);
}

/** The login step-up: same call, different moment. */
export async function submitChallenge(factorId: string, code: string): Promise<void> {
  const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
  fail(error, 'That code was not accepted');
}

/** Turning it off needs a live aal2 session, which Supabase enforces — a
 *  stolen aal1 token cannot quietly remove the thing protecting the account. */
export async function removeFactor(factorId: string): Promise<void> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  fail(error, 'Could not turn off two-factor');
}
